import type { VideoBroadcastPlaybackTimeSource, VideoBroadcastStateParams } from '@repo/go-widgets';

import type { TCreateWidgetOptions } from 'src/entities/tab/TabEntity';

import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import type { VideobroadcastWidgetEntity } from '../videobroadcast-widget/VideobroadcastWidget.entity';
import type { WellLogsWidgetEntity } from '../well-logs-widget/WellLogsWidget.entity';

type SyncSource = { type: 'wellLogs'; widgetId: string } | { type: 'video'; widgetId: string };
type SyncGroup = { key: string; groupId: string; wellId: number };
type LinkedWidget = WellLogsWidgetEntity | VideobroadcastWidgetEntity;
type VideoInterval = { fromMs: number; toMs: number };
type VideoPlaybackMode = 'archive' | 'live';
type GroupSyncState = {
  activeSource?: SyncSource;
  pendingFollowerSeek?: ReturnType<typeof setTimeout>;
  videoInterval?: VideoInterval;
  followerGuardUntilByWidget: Map<string, number>;
  videoPlaybackModeByWidget: Map<string, VideoPlaybackMode>;
};

type CreateVideoWidget = (
  wellId: number | null,
  stateParams: Partial<VideoBroadcastStateParams> | null,
  options?: TCreateWidgetOptions,
) => void;

const FOLLOWER_SEEK_GUARD_MS = 2000;
const TIMELINE_FOLLOWER_SEEK_DEBOUNCE_MS = 500;

const coordinators = new WeakMap<GroupSelectStore, LinkedVideoTimeCoordinator>();

export function getLinkedVideoTimeCoordinator(groupSelectStore: GroupSelectStore): LinkedVideoTimeCoordinator {
  let coordinator = coordinators.get(groupSelectStore);

  if (!coordinator) {
    coordinator = new LinkedVideoTimeCoordinator(groupSelectStore);
    coordinators.set(groupSelectStore, coordinator);
  }

  return coordinator;
}

export class LinkedVideoTimeCoordinator {
  private groupStates = new Map<string, GroupSyncState>();

  constructor(private readonly groupSelectStore: GroupSelectStore) {}

  refreshVideoSyncForWidget(widget: LinkedWidget): void {
    const group = this.getSyncGroup(widget);

    if (!group) return;

    this.refreshVideoSyncForGroupState(group);
  }

  refreshVideoSyncForGroup(groupId: string | null, wellId: number | null): void {
    if (groupId === null || wellId === null) return;

    this.refreshVideoSyncForGroupState({
      key: `${groupId}:${wellId}`,
      groupId,
      wellId,
    });
  }

  ensureVideoForWellLogs(wellLogsWidget: WellLogsWidgetEntity, createVideoWidget: CreateVideoWidget): void {
    const group = this.getSyncGroup(wellLogsWidget);

    if (!group) return;

    const videos = this.getVideoWidgets(group);

    if (videos.length > 0) {
      videos.forEach((video) => this.syncVideoWellboreOnce(video, wellLogsWidget));
      this.refreshVideoSyncForGroupState(group);
      this.activateVideoSourceOnSyncStart(group, videos);
      return;
    }

    const wellboreId = this.getWellLogsWellboreId(wellLogsWidget);
    const stateParams = wellboreId === null ? null : { wellboreId };

    createVideoWidget(group.wellId, stateParams, { groupId: group.groupId });
  }

  publishFromWellLogs(wellLogsWidget: WellLogsWidgetEntity, timeMs: number): void {
    const group = this.getSyncGroup(wellLogsWidget);

    if (!group) return;

    const state = this.getGroupState(group);
    const videoInterval = state.videoInterval;

    if (!videoInterval || this.isTimeOutsideVideoInterval(timeMs, videoInterval)) {
      this.clearVideoIntervals(group);
    }

    const sourceVideo = this.getVideoWidgets(group)[0];

    state.activeSource = sourceVideo
      ? { type: 'video', widgetId: sourceVideo.uuid }
      : { type: 'wellLogs', widgetId: wellLogsWidget.uuid };
    this.setWellLogsMarkers(group, timeMs, 'timeline', wellLogsWidget.uuid);
    this.seekFollowerVideos(group, null, timeMs);
  }

  publishIntervalFromWellLogs(wellLogsWidget: WellLogsWidgetEntity, interval: VideoInterval): void {
    const group = this.getSyncGroup(wellLogsWidget);

    if (!group) return;

    const normalizedInterval = this.normalizeVideoInterval(interval);

    if (!normalizedInterval) return;

    const videos = this.getVideoWidgets(group);
    const sourceVideo = videos[0];
    const state = this.getGroupState(group);

    state.videoInterval = normalizedInterval;
    state.activeSource = sourceVideo
      ? { type: 'video', widgetId: sourceVideo.uuid }
      : { type: 'wellLogs', widgetId: wellLogsWidget.uuid };

    this.clearPendingFollowerSeek(group);
    this.setWellLogsLinkedSyncEnabled(group, true, normalizedInterval.fromMs);
    this.setWellLogsMarkers(group, normalizedInterval.fromMs, 'timeline', wellLogsWidget.uuid);
    this.setWellLogsVideoIntervals(group, normalizedInterval);
    this.showVideoIntervalOnVideos(group, normalizedInterval);
  }

  publishIntervalChangeFromVideo(videoWidget: VideobroadcastWidgetEntity, interval: VideoInterval | null): void {
    const group = this.getSyncGroup(videoWidget);

    if (!group) return;

    if (this.isProgrammaticFollowerEvent(group, videoWidget.uuid)) return;

    if (!interval) {
      this.clearVideoIntervals(group, { sourceVideoWidgetId: videoWidget.uuid });
      return;
    }

    const normalizedInterval = this.normalizeVideoInterval(interval);

    if (!normalizedInterval) return;

    const state = this.getGroupState(group);

    state.videoInterval = normalizedInterval;
    state.activeSource = { type: 'video', widgetId: videoWidget.uuid };

    this.clearPendingFollowerSeek(group);
    this.setWellLogsLinkedSyncEnabled(group, true, normalizedInterval.fromMs);
    this.setWellLogsMarkers(group, normalizedInterval.fromMs, 'timeline');
    this.setWellLogsVideoIntervals(group, normalizedInterval);
    this.showVideoIntervalOnVideos(group, normalizedInterval, {
      onlyMissing: true,
      sourceVideoWidgetId: videoWidget.uuid,
    });
  }

  publishFromVideo(
    videoWidget: VideobroadcastWidgetEntity,
    timeMs: number | null,
    source: VideoBroadcastPlaybackTimeSource,
  ): void {
    const group = this.getSyncGroup(videoWidget);

    if (!group) return;

    if (!this.hasEnabledWellLogsSync(group)) {
      this.disableVideoSync(group);
      return;
    }

    const previousPlaybackMode = this.getKnownVideoPlaybackMode(group, videoWidget.uuid);
    const playbackMode = this.getVideoPlaybackMode(videoWidget);
    this.setKnownVideoPlaybackMode(group, videoWidget.uuid, playbackMode);

    if (this.isProgrammaticFollowerEvent(group, videoWidget.uuid)) {
      return;
    }

    if (timeMs === null || playbackMode === 'live') {
      if (this.shouldPublishLiveModeFromVideo(group, videoWidget.uuid, source, previousPlaybackMode)) {
        this.publishLiveModeFromVideo(group, videoWidget.uuid, timeMs);
      }

      return;
    }

    const state = this.getGroupState(group);
    const activeSource = state.activeSource;
    const shouldTakeVideoSource = source === 'timeline' || activeSource?.type !== 'video';

    if (shouldTakeVideoSource) {
      state.activeSource = { type: 'video', widgetId: videoWidget.uuid };
    }

    if (!this.isActiveVideoSource(state, videoWidget.uuid)) {
      return;
    }

    videoWidget.state?.setLinkedVideoSyncEnabled(true);
    this.setWellLogsMarkers(group, timeMs, source);

    if (shouldTakeVideoSource) {
      if (source === 'timeline') {
        this.scheduleFollowerVideosSeek(group, videoWidget.uuid, timeMs);
      } else {
        this.seekFollowerVideos(group, videoWidget.uuid, timeMs);
      }
    }
  }

  private getSyncGroup(widget: LinkedWidget): SyncGroup | null {
    if (widget.groupId === null || widget.wellId === null) {
      return null;
    }

    if (widget.wellIndexType !== 'LOG_TIME') {
      return null;
    }

    return {
      key: `${widget.groupId}:${widget.wellId}`,
      groupId: widget.groupId,
      wellId: widget.wellId,
    };
  }

  private getGroupState(group: SyncGroup): GroupSyncState {
    let state = this.groupStates.get(group.key);

    if (!state) {
      state = {
        followerGuardUntilByWidget: new Map(),
        videoPlaybackModeByWidget: new Map(),
      };
      this.groupStates.set(group.key, state);
    }

    return state;
  }

  private getGroupWidgets(group: SyncGroup) {
    return this.groupSelectStore.groupManager.getWidgetsByGroupId(group.groupId);
  }

  private getVideoWidgets(group: SyncGroup): VideobroadcastWidgetEntity[] {
    return this.getGroupWidgets(group).filter(
      (widget): widget is VideobroadcastWidgetEntity =>
        widget.type === 'videobroadcast-widget' && 'wellId' in widget && widget.wellId === group.wellId,
    );
  }

  private getWellLogsWidgets(group: SyncGroup): WellLogsWidgetEntity[] {
    return this.getGroupWidgets(group).filter(
      (widget): widget is WellLogsWidgetEntity =>
        widget.type === 'well-logs-widget' &&
        'wellId' in widget &&
        'wellIndexType' in widget &&
        widget.wellId === group.wellId &&
        widget.wellIndexType === 'LOG_TIME',
    );
  }

  private getWellLogsWellboreId(wellLogsWidget: WellLogsWidgetEntity): number | null {
    const wellboreId = wellLogsWidget.stateParams.wellboreId;

    return typeof wellboreId === 'number' ? wellboreId : null;
  }

  private hasEnabledWellLogsSync(group: SyncGroup): boolean {
    return this.getWellLogsWidgets(group).some((widget) => this.isWellLogsSyncEnabled(widget));
  }

  private isWellLogsSyncEnabled(widget: WellLogsWidgetEntity): boolean {
    if (widget.state) {
      return Boolean(widget.state.linkedVideoSync?.enabled);
    }

    return Boolean(widget.stateParams.linkedVideoSyncEnabled);
  }

  private getWellLogsSyncTime(group: SyncGroup): number | null {
    for (const widget of this.getWellLogsWidgets(group)) {
      if (!this.isWellLogsSyncEnabled(widget)) continue;

      if (widget.state) {
        const runtimeTimeMs = widget.state.linkedVideoSync?.markerTimeMs;

        if (typeof runtimeTimeMs === 'number') {
          return runtimeTimeMs;
        }

        continue;
      }

      const stateTimeMs = widget.stateParams.linkedVideoSyncTimeMs;

      if (typeof stateTimeMs === 'number') {
        return stateTimeMs;
      }
    }

    return null;
  }

  private refreshVideoSyncForGroupState(group: SyncGroup): void {
    if (!this.hasEnabledWellLogsSync(group)) {
      this.disableVideoSync(group);
      return;
    }

    const state = this.getGroupState(group);

    this.rememberVideoPlaybackModes(group);
    this.setVideosLinkedSyncEnabled(group, true, this.getWellLogsSyncTime(group));

    if (state.videoInterval) {
      this.setWellLogsLinkedSyncEnabled(group, true, state.videoInterval.fromMs);
      this.setWellLogsVideoIntervals(group, state.videoInterval);
      this.showVideoIntervalOnVideos(group, state.videoInterval, { onlyMissing: true });
    }
  }

  private disableVideoSync(group: SyncGroup): void {
    this.clearPendingFollowerSeek(group);

    const state = this.getGroupState(group);
    state.activeSource = undefined;
    state.videoInterval = undefined;
    state.followerGuardUntilByWidget.clear();
    state.videoPlaybackModeByWidget.clear();

    this.setWellLogsVideoIntervals(group, null);
    this.setVideosLinkedSyncEnabled(group, false);
  }

  private setVideosLinkedSyncEnabled(
    group: SyncGroup,
    enabled: boolean,
    timeMs: number | null = null,
    sourceVideoWidgetId?: string,
  ): void {
    this.getVideoWidgets(group).forEach((widget) => {
      if (!widget.state) return;

      const syncTimeMs = widget.uuid === sourceVideoWidgetId ? null : timeMs;
      widget.state.setLinkedVideoSyncEnabled(enabled, syncTimeMs);
    });
  }

  private normalizeVideoInterval(interval: VideoInterval): VideoInterval | null {
    const fromMs = Math.min(interval.fromMs, interval.toMs);
    const toMs = Math.max(interval.fromMs, interval.toMs);

    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) {
      return null;
    }

    return { fromMs, toMs };
  }

  private isSameVideoInterval(currentInterval: VideoInterval | null | undefined, nextInterval: VideoInterval): boolean {
    return currentInterval?.fromMs === nextInterval.fromMs && currentInterval.toMs === nextInterval.toMs;
  }

  private isTimeOutsideVideoInterval(timeMs: number, interval: VideoInterval): boolean {
    return timeMs < interval.fromMs || timeMs > interval.toMs;
  }

  private showVideoIntervalOnVideos(
    group: SyncGroup,
    interval: VideoInterval,
    options: { onlyMissing?: boolean; sourceVideoWidgetId?: string } = {},
  ): void {
    this.getVideoWidgets(group).forEach((widget) => {
      if (widget.uuid === options.sourceVideoWidgetId) return;
      if (!widget.state) return;

      if (options.onlyMissing && this.isSameVideoInterval(widget.state.videoInterval, interval)) return;

      this.runWithFollowerGuard(group, widget, () => {
        widget.state?.setLinkedVideoSyncEnabled(true);
        widget.state?.showVideoInterval(interval);
      });
    });
  }

  private clearVideoIntervals(group: SyncGroup, options: { sourceVideoWidgetId?: string } = {}): void {
    this.getGroupState(group).videoInterval = undefined;
    this.setWellLogsVideoIntervals(group, null);

    this.getVideoWidgets(group).forEach((widget) => {
      if (widget.uuid === options.sourceVideoWidgetId) return;

      this.runWithFollowerGuard(group, widget, () => {
        this.clearVideoIntervalOnWidget(widget);
      });
    });
  }

  private setWellLogsVideoIntervals(group: SyncGroup, interval: VideoInterval | null): void {
    this.getWellLogsWidgets(group).forEach((widget) => {
      widget.state?.linkedVideoSync?.setVideoInterval(interval);
    });
  }

  private setWellLogsLinkedSyncEnabled(group: SyncGroup, enabled: boolean, timeMs: number | null = null): void {
    this.getWellLogsWidgets(group).forEach((widget) => {
      if (this.isWellLogsSyncEnabled(widget) === enabled) return;

      if (widget.state) {
        widget.state.linkedVideoSync?.setEnabled(enabled);
        return;
      }

      widget.setStateParams({
        ...widget.stateParams,
        linkedVideoSyncEnabled: enabled,
        linkedVideoSyncTimeMs: enabled ? timeMs : null,
      });
    });
  }

  private clearVideoIntervalOnWidget(widget: VideobroadcastWidgetEntity): void {
    const state = widget.state;

    if (!state) return;

    state.clearVideoInterval();

    state.selectedCameras.forEach((camera) => {
      if (state.getPlayerSettings(camera)?.timelineViewport != null) return;

      state.setPlayerSettings(camera, { timelineViewport: { mode: 'auto' } }, { immediate: true });
    });
  }

  private syncVideoWellboreOnce(videoWidget: VideobroadcastWidgetEntity, wellLogsWidget: WellLogsWidgetEntity): void {
    const wellboreId = this.getWellLogsWellboreId(wellLogsWidget);

    if (wellboreId === null) return;

    if (videoWidget.state) {
      videoWidget.state.switchWellbore(wellboreId);
      return;
    }

    videoWidget.setStateParams({
      ...videoWidget.stateParams,
      wellboreId,
    });
  }

  private getVideoPlaybackTime(videoWidget: VideobroadcastWidgetEntity): number | null {
    if (videoWidget.state) {
      if (videoWidget.state.playbackMode === 'live') {
        return null;
      }

      return videoWidget.state.currentPlaybackTimeMs;
    }

    if (videoWidget.stateParams.playbackMode !== 'archive') {
      return null;
    }

    return videoWidget.stateParams.playbackTimeMs ?? null;
  }

  private isVideoInLiveMode(videoWidget: VideobroadcastWidgetEntity): boolean {
    if (videoWidget.state) {
      return videoWidget.state.playbackMode === 'live';
    }

    return videoWidget.stateParams.playbackMode !== 'archive';
  }

  private getVideoPlaybackMode(videoWidget: VideobroadcastWidgetEntity): VideoPlaybackMode {
    return this.isVideoInLiveMode(videoWidget) ? 'live' : 'archive';
  }

  private getKnownVideoPlaybackMode(group: SyncGroup, videoWidgetId: string): VideoPlaybackMode | undefined {
    return this.getGroupState(group).videoPlaybackModeByWidget.get(videoWidgetId);
  }

  private setKnownVideoPlaybackMode(group: SyncGroup, videoWidgetId: string, playbackMode: VideoPlaybackMode): void {
    this.getGroupState(group).videoPlaybackModeByWidget.set(videoWidgetId, playbackMode);
  }

  private rememberVideoPlaybackModes(group: SyncGroup): void {
    this.getVideoWidgets(group).forEach((videoWidget) => {
      this.setKnownVideoPlaybackMode(group, videoWidget.uuid, this.getVideoPlaybackMode(videoWidget));
    });
  }

  private activateVideoSourceOnSyncStart(group: SyncGroup, videos: VideobroadcastWidgetEntity[]): void {
    const sourceVideo = videos.find((video) => this.getVideoPlaybackTime(video) !== null) ?? videos[0];
    const sourceTimeMs = this.getVideoPlaybackTime(sourceVideo);

    const state = this.getGroupState(group);

    state.activeSource = { type: 'video', widgetId: sourceVideo.uuid };

    this.setVideosLinkedSyncEnabled(group, true);

    if (sourceTimeMs === null) return;

    this.setWellLogsMarkers(group, sourceTimeMs, 'player');
    this.seekFollowerVideos(group, sourceVideo.uuid, sourceTimeMs);
  }

  private isActiveVideoSource(state: GroupSyncState, videoWidgetId: string): boolean {
    return state.activeSource?.type === 'video' && state.activeSource.widgetId === videoWidgetId;
  }

  private shouldPublishLiveModeFromVideo(
    group: SyncGroup,
    videoWidgetId: string,
    source: VideoBroadcastPlaybackTimeSource,
    previousPlaybackMode: VideoPlaybackMode | undefined,
  ): boolean {
    const state = this.getGroupState(group);

    if (source === 'timeline') return true;
    if (this.isActiveVideoSource(state, videoWidgetId)) return true;
    if (state.activeSource?.type !== 'video') return true;

    return previousPlaybackMode !== 'live';
  }

  private setWellLogsMarkers(
    group: SyncGroup,
    timeMs: number,
    source: VideoBroadcastPlaybackTimeSource,
    sourceWellLogsWidgetId?: string,
  ): void {
    this.getWellLogsWidgets(group).forEach((widget) => {
      if (widget.uuid === sourceWellLogsWidgetId) return;

      widget.state?.linkedVideoSync?.setPlaybackTime(timeMs, source);
    });
  }

  private clearWellLogsMarkers(group: SyncGroup): void {
    this.getWellLogsWidgets(group).forEach((widget) => {
      widget.state?.linkedVideoSync?.clearPlaybackTime();
    });
  }

  private publishLiveModeFromVideo(group: SyncGroup, sourceVideoWidgetId: string, playbackTimeMs: number | null): void {
    const state = this.getGroupState(group);

    this.clearPendingFollowerSeek(group);
    state.activeSource = { type: 'video', widgetId: sourceVideoWidgetId };
    this.setVideosLinkedSyncEnabled(group, true, null, sourceVideoWidgetId);

    if (playbackTimeMs === null) {
      this.clearWellLogsMarkers(group);
    } else {
      this.setWellLogsMarkers(group, playbackTimeMs, 'player');
    }

    this.getVideoWidgets(group).forEach((widget) => {
      if (widget.uuid === sourceVideoWidgetId) return;
      if (widget.state?.playbackMode === 'live') return;

      this.runWithFollowerGuard(group, widget, () => widget.state?.setLiveMode());
    });
  }

  private seekFollowerVideos(group: SyncGroup, sourceVideoWidgetId: string | null, timeMs: number): void {
    this.clearPendingFollowerSeek(group);

    this.getVideoWidgets(group).forEach((widget) => {
      if (widget.uuid === sourceVideoWidgetId) return;

      this.runWithFollowerGuard(group, widget, () => {
        widget.state?.setLinkedVideoSyncEnabled(true);
        widget.state?.seekToTime(timeMs);
      });
    });
  }

  private scheduleFollowerVideosSeek(group: SyncGroup, sourceVideoWidgetId: string | null, timeMs: number): void {
    const state = this.getGroupState(group);

    this.clearPendingFollowerSeek(group);

    const timeoutId = setTimeout(() => {
      state.pendingFollowerSeek = undefined;
      this.seekFollowerVideos(group, sourceVideoWidgetId, timeMs);
    }, TIMELINE_FOLLOWER_SEEK_DEBOUNCE_MS);

    state.pendingFollowerSeek = timeoutId;
  }

  private clearPendingFollowerSeek(group: SyncGroup): void {
    const state = this.getGroupState(group);
    const timeoutId = state.pendingFollowerSeek;

    if (!timeoutId) return;

    clearTimeout(timeoutId);
    state.pendingFollowerSeek = undefined;
  }

  private runWithFollowerGuard(group: SyncGroup, widget: VideobroadcastWidgetEntity, action: () => void): void {
    this.getGroupState(group).followerGuardUntilByWidget.set(widget.uuid, Date.now() + FOLLOWER_SEEK_GUARD_MS);
    action();
  }

  private isProgrammaticFollowerEvent(group: SyncGroup, widgetId: string): boolean {
    const state = this.getGroupState(group);
    const expiresAt = state.followerGuardUntilByWidget.get(widgetId);

    if (!expiresAt) return false;

    if (Date.now() > expiresAt) {
      state.followerGuardUntilByWidget.delete(widgetId);
      return false;
    }

    return true;
  }
}
